export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Firefighter Foundations, PPE & Equipment Atlas',
    subtitle: 'Essential Gear, Protective Systems, Tools & Firefighter Readiness',
    intro: [
      'Every firefighter needs a solid foundation.',
      'Before students advance into more complex operations, they must understand the equipment, protective systems and tools used throughout the fire service.',
      'This volume gives instructors a complete visual reference for teaching essential firefighter knowledge.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Fire service fundamentals',
      'Firefighter roles and responsibilities',
      'Personal protective equipment',
      'Helmet components',
      'Protective clothing',
      'Gloves and boots',
      'SCBA components',
      'Breathing apparatus awareness',
      'Firefighter equipment identification',
      'Hand tools',
      'Tool functions and limitations',
      'Apparatus awareness',
      'Equipment organization',
      'Inspection concepts',
      'Safety principles',
      'Firefighter readiness',
      'Common equipment errors',
      'Instructor reference sheets',
    ],
    result:
      'Give your students a clearer understanding of essential firefighter equipment through structured visual explanations and professional reference material.',
  },
  {
    number: 'Volume 02',
    title: 'Hose, Water Supply & Fireground Operations',
    subtitle: 'Visual Training for Hose Systems, Water Movement, Nozzles & Fireground Organization',
    intro: [
      'Fireground operations involve multiple systems working together.',
      'Understanding water movement, hose configurations, equipment functions and operational organization is essential for firefighter education.',
      'This volume transforms these topics into organized visual training resources.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Fire hose types',
      'Hose components',
      'Couplings and connections',
      'Hose configurations',
      'Nozzle identification',
      'Nozzle functions',
      'Water supply fundamentals',
      'Hydrant awareness',
      'Hose deployment concepts',
      'Water movement diagrams',
      'Pump and apparatus awareness',
      'Fireground organization',
      'Operational zones',
      'Team coordination',
      'Communication principles',
      'Fireground equipment',
      'Common operational errors',
      'Instructor teaching references',
    ],
    result:
      'Make hose systems, water supply and fireground organization easier for your students to visualize, understand and discuss.',
  },
  {
    number: 'Volume 03',
    title: 'Ladders, Forcible Entry, Ventilation & Building Access',
    subtitle: 'Visual Systems for Access, Entry, Positioning & Ventilation Awareness',
    intro: [
      'Building access and ventilation concepts can be difficult to explain using text alone.',
      'Students need to understand structures, openings, equipment, positioning, hazards and the purpose of different procedures.',
      'This volume organizes those subjects into clear visual instructional material.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Fire service ladder types',
      'Ladder components',
      'Ladder inspection awareness',
      'Ladder positioning concepts',
      'Building access points',
      'Door and window identification',
      'Building openings',
      'Forcible-entry tool identification',
      'Tool functions and limitations',
      'Entry planning concepts',
      'Building orientation',
      'Ventilation principles',
      'Airflow awareness',
      'Structural considerations',
      'Access hazards',
      'Common mistakes',
      'Correct-vs-incorrect comparisons',
      'Instructor reference diagrams',
    ],
    result:
      'Give your students a clearer understanding of building access, ladders and ventilation through professional visual references and structured instructor-led lessons.',
  },
  {
    number: 'Volume 04',
    title: 'Search, Rescue & Structural Fire Operations',
    subtitle:
      'Visual Training for Search, Rescue Priorities, Fireground Awareness & Structural Response',
    intro: [
      'Search and rescue training requires more than simply explaining a procedure.',
      'Firefighters must understand building orientation, team coordination, communication, rescue priorities, environmental hazards and changing operational conditions.',
      'This volume brings those concepts together through detailed building cutaways, search maps, training scenarios and practical instructor exercises.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Building orientation',
      'Structural layouts',
      'Floor plan interpretation',
      'Entry and exit awareness',
      'Search team organization',
      'Team integrity',
      'Search area mapping',
      'Search pattern concepts',
      'Victim-location awareness',
      'Rescue priorities',
      'Team communication',
      'Accountability boards',
      'Limited-visibility awareness',
      'Structural fire conditions',
      'Hazard recognition',
      'Changing-condition scenarios',
      'Exit problem scenarios',
      'Multi-floor structures',
      'Apartment building scenarios',
      'Commercial building scenarios',
      'Common search training errors',
      'Instructor correction cues',
      'Classroom decision-making exercises',
      'Training assessments',
      'Quick-reference sheets',
    ],
    sequence: 'A VISUAL TRAINING EXPERIENCE YOUR STUDENTS CAN FOLLOW.',
    result:
      'Turn complex search, rescue and structural fire concepts into professional, structured training material that instructors can present, discuss and assess more effectively.',
    popular: true,
  },
  {
    number: 'Volume 05',
    title: 'Vehicle Rescue & Technical Incident Atlas',
    subtitle:
      'Visual Reference for Vehicle Stabilization, Rescue Zones, Extrication Awareness & Complex Incidents',
    intro: [
      'Firefighters must also recognize and understand emergencies beyond structural fires.',
      'Vehicle incidents, technical hazards and specialized response situations require clear knowledge of equipment, incident organization and safety considerations.',
      'This volume provides visual reference material for these important subjects.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Vehicle construction awareness',
      'Vehicle components',
      'Structural vehicle anatomy',
      'Rescue scene organization',
      'Hazard identification',
      'Stabilization concepts',
      'Extrication equipment identification',
      'Rescue tool functions',
      'Vehicle access considerations',
      'Rescue zones',
      'Electrical vehicle awareness',
      'Fuel-related hazards',
      'Technical incident recognition',
      'Specialized equipment',
      'Hazardous-material awareness',
      'Incident communication',
      'Technical response limitations',
      'Instructor scenarios',
      'Decision-making exercises',
    ],
    result:
      'Expand your teaching resources with professional visual references covering vehicle emergencies, technical incidents and specialized hazard awareness.',
  },
  {
    number: 'Volume 06',
    title: 'Instructor Drills, Scenarios, Assessments & Decision Tools',
    subtitle: 'Turn Fire Service Knowledge Into Structured, Repeatable Training',
    intro: [
      'Knowing the subject is important.',
      'But knowing how to organize and teach it effectively is what turns information into training.',
      'This volume helps instructors transform firefighter knowledge into structured learning experiences.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Instructor training frameworks',
      'Lesson objectives',
      'Session planning',
      'Training organization',
      'Student progression',
      'Instructor-led drills',
      'Controlled training exercises',
      'Classroom scenarios',
      'Decision-making activities',
      'Demonstration guidance',
      'Instructor observation cues',
      'Common trainee errors',
      'Corrective feedback',
      'Beginner-level adaptations',
      'Intermediate-level adaptations',
      'Advanced decision challenges',
      'Communication assessments',
      'Performance evaluation',
      'Progress tracking',
      'After-action review frameworks',
      'Quick-reference instructor tools',
    ],
    result:
      'Stop developing every instructional activity from scratch and start using a more organized framework for teaching, observing and evaluating firefighter development.',
  },
]
